// src/pages/CollegeDetail.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography
} from "@mui/material";
import cap1 from "../data/capf_1.json";  // adjust path if needed

// [PATCH] Optimal Cutoff Helper: Priorities Tech -> Falls back to Highest Cutoff Branch
function getGOPENSCutoffsByCollegeId(collegeId) {
  // 1. Define Priority Groups for Tech Branches
  // We check these groups in order. If found, we show the best one from that group.
  const priorityGroups = [
    // Group 1: CS, AI, Data Science, IoT, Cyber Security
    ["computer", "cse", "artificial", "data", "machine", "cyber", "iot", "robotics"],
    // Group 2: IT
    ["information", "it"],
    // Group 3: Electronics / EnTC
    ["electronics", "entc", "communication", "vlsi"]
  ];

  // 2. Find college in the main data
  const college = collegesData.find(
    (col) => col.Institute_Code?.trim() === collegeId?.trim()
  );

  if (!college || !college.Courses) return [];

  // --- Helper: Get the best 'Open' cutoff for a single course ---
  // Returns null if no data, or an object with rank/percentile if found.
  // Checks GOPENS (State) -> GOPENH (Home) -> GOPENO (Other) and picks the highest percentile.
  const getBestOpenCutoff = (course) => {
    if (!course.CAP1 || !Array.isArray(course.CAP1)) return null;

    const categories = ["GOPENS", "GOPENH", "GOPENO"];
    let best = null;
    let maxPercentile = -1;

    for (const cat of categories) {
      const match = course.CAP1.find((c) => c[0] === cat);
      if (match) {
        const rawPerc = match[2]?.replace(/[()]/g, "") || "0";
        const val = parseFloat(rawPerc);
        
        // We want the highest percentile to represent the course's competitiveness
        if (!isNaN(val) && val > maxPercentile) {
          maxPercentile = val;
          best = {
            courseName: course.Course_Name || "",
            rank: match[1],
            percentile: rawPerc, // Keep original string for display consistency
            val: val // numeric for sorting
          };
        }
      }
    }
    return best;
  };

  // 3. Strategy A: Try to find a Tech Branch (Priority 1 -> 2 -> 3)
  for (const group of priorityGroups) {
    const groupMatches = [];

    // Find all courses matching the current priority group
    for (const course of college.Courses) {
      const isMatch = group.some((keyword) => 
        (course.Course_Name || "").toLowerCase().includes(keyword.toLowerCase())
      );
      
      if (isMatch) {
        const cutoffData = getBestOpenCutoff(course);
        if (cutoffData) groupMatches.push(cutoffData);
      }
    }

    // If we found any valid courses in this priority group, pick the best one and RETURN
    if (groupMatches.length > 0) {
      // Sort by percentile descending (highest first)
      groupMatches.sort((a, b) => b.val - a.val);
      return [groupMatches[0]]; // Return top result
    }
  }

  // 4. Strategy B (Fallback): No Tech branches found? 
  // Find the single branch with the HIGHEST cutoff across the ENTIRE college.
  const allCourseCutoffs = [];

  for (const course of college.Courses) {
    const cutoffData = getBestOpenCutoff(course);
    if (cutoffData) {
      allCourseCutoffs.push(cutoffData);
    }
  }

  if (allCourseCutoffs.length > 0) {
    // Sort all courses by percentile descending
    allCourseCutoffs.sort((a, b) => b.val - a.val);
    
    // Return the single highest one (e.g., Food Tech or Pharmacy)
    return [allCourseCutoffs[0]];
  }

  return []; // Truly no data available
}
import cap2 from "../data/capf_2.json";
import cap3 from "../data/capf_3.json";
import cap4 from "../data/capf_4.json"

import {
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  CircularProgress,
  SwipeableDrawer,

  Divider,
  Tooltip,

  Drawer,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSearchModal } from "../context/SearchModalContext";
import collegesData from "../data/all.json";
import getUniversityLogo from "../constants/universityLogos";
import { getCourseCutoffs as fallbackGetCourseCutoffs } from "../components/capCutoff";
import { useCompare } from "../context/CompareContext";
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";


// CAP data mapping
const capMap = { 1: cap1, 2: cap2, 3: cap3 ,4: cap4};
const normalize = (s) => (s == null ? "" : String(s).trim());

// Parse CAP array row
const parseCutoffArray = (arr) => {
  const [categoryRaw, rankRaw, percRaw] = arr;
  const category = normalize(categoryRaw);
  const rank = normalize(rankRaw) || "—";
  let percentile = "—";
  if (typeof percRaw === "string") {
  const cleaned = percRaw.replace(/[()]/g, "").trim();
  percentile = cleaned || "—";
}
else if (typeof percRaw === "number") {
  percentile = String(percRaw);
}
  return { Category: category, Rank: rank, Percentile: percentile };
};

const ALL_FILTERS = ["State Level", "Home University", "Other"];

const CollegeDetail = () => {
  const { instituteCode } = useParams();
  const navigate = useNavigate();

  // base data
  const [college, setCollege] = useState(null);

  // accordion
  const [expandedIndex, setExpandedIndex] = useState(false);

  // cutoff drawer
  const [cutoffDrawerOpen, setCutoffDrawerOpen] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [currentChoiceCode, setCurrentChoiceCode] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [cutoffRows, setCutoffRows] = useState([]);
  const [loadingCutoff, setLoadingCutoff] = useState(false);

  // filters (multi-select). default = all selected
  const [selectedFilters, setSelectedFilters] = useState([...ALL_FILTERS]);

  const [videoOpen, setVideoOpen] = useState(false);

  // compare context
  const { addCollege,setCompareMode , removeCollege, colleges } = useCompare();
  const { openSearch } = useSearchModal();


const handleAddToCompare = () => {

  const nextCount = colleges.length + 1; // ✅ Predict next state

  addCollege({
    id: college.Institute_Code,
    name: college.Institute_Name,
    university: college.University,
    autonomyStatus: college.Autonomy_Status,
    district: college.District,
    fee: college.Fee,
    intake: college.Total_Intake,
    webAddress: college.Web_Address,
  });
  setCompareMode(true);
  // First → Open search
  if (nextCount === 1) {
    openSearch();
    return;
  }

  // Second → Go to compare
  if (nextCount === 2) {
    navigate("/compare");
  }
};


  // load college
  useEffect(() => {
    const found = collegesData.find(
      (c) => normalize(c.Institute_Code) === normalize(instituteCode)
    );
    setCollege(found || null);
    setExpandedIndex(false);
    setCurrentCourse(null);
    setCurrentChoiceCode(null);
    setCutoffDrawerOpen(false);
    
    setCutoffRows([]);
    setSelectedRound(null);
  }, [instituteCode]);

  const courses = useMemo(() => college?.Courses || [], [college]);

  /** Get course choice code */
  const getCourseCode = (course) =>
    normalize(course?.Choice_Code ?? course?.Course_Id ?? course?.ChoiceCode ?? "");

  /** toggle filter chip */
  const toggleFilter = (f) => {
    setSelectedFilters((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    );
  };

  const classifyCategory = (rawCategory) => {
    const cat = normalize(rawCategory).toUpperCase();
    const lastChar = cat.slice(-1);

    const isHome = lastChar === "H"; // e.g., OPENH, SCH
    const isState = cat === "AI" || lastChar === "S"; // e.g., OPENS, OBCS, AI
    const isOther = !isState && !isHome; // remaining ones

    return { isState, isHome, isOther };
  };

  /** checks whether a row matches the currently selectedFilters (multi-select union) */
  const rowMatchesSelectedFilters = (row) => {
    if (!selectedFilters || selectedFilters.length === 0) return false;

    const { isState, isHome, isOther } = classifyCategory(row.Category || "");

    let match = false;
    if (isState && selectedFilters.includes("State Level")) match = true;
    if (isHome && selectedFilters.includes("Home University")) match = true;
    if (isOther && selectedFilters.includes("Other")) match = true;

    return match;
  };

  /** load cutoff data */
  const loadCutoff = (courseObj, courseCode, round) => {
    if (!courseCode) return;
    setLoadingCutoff(true);
    setSelectedRound(round);
    setCutoffRows([]);
    setCutoffDrawerOpen(false);

    const capData = capMap[Number(round)] || [];
    const wanted = normalize(courseCode);
    const found = capData.find((entry) => normalize(entry.Course_Id) === wanted);

    if (found && Array.isArray(found.cutoff)) {
      const parsed = found.cutoff.map((r) => parseCutoffArray(r));
      setCutoffRows(parsed);
      setLoadingCutoff(false);
      setCutoffDrawerOpen(true);
      setCurrentCourse(courseObj);
      setCurrentChoiceCode(courseCode);
      return;
    }

    // fallback
    if (typeof fallbackGetCourseCutoffs === "function") {
      try {
        const fallbackRows = fallbackGetCourseCutoffs({
          round: Number(round),
          courseId: wanted,
        });
        if (Array.isArray(fallbackRows) && fallbackRows.length > 0) {
          const parsed = fallbackRows.map((r) => ({
            Category: r.code ?? r.Category ?? "—",
            Rank: r.rank ?? r.Rank ?? "—",
            Percentile:
              r.percentile != null
                ? (Number(r.percentile).toFixed
                    ? Number(r.percentile).toFixed(2)
                    : r.percentile)
                : r.percentile ?? "—",
          }));
          setCutoffRows(parsed);
          setLoadingCutoff(false);
          setCutoffDrawerOpen(true);
          setCurrentCourse(courseObj);
          setCurrentChoiceCode(courseCode);
          return;
        }
      } catch (err) {}
    }

    setCutoffRows([]);
    setLoadingCutoff(false);
    setCutoffDrawerOpen(true);
    setCurrentCourse(courseObj);
    setCurrentChoiceCode(courseCode);
  };

  /** copy text */
  const copyToClipboard = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  };

  /** open website */
const openWebsite = (url) => {
  if (!url) return;

  let u = String(url).trim().replace(/\s+/g, "");

  // If protocol already exists, open directly
  if (/^https?:\/\//i.test(u)) {
    window.open(u, "_blank", "noopener");
    return;
  }

  // Prefer HTTP for government / college sites
  window.open("http://" + u, "_blank", "noopener");
};



  if (!college) {
    return <Typography sx={{ p: 3 }}>College not found.</Typography>;
  }

  return (
    <Box sx={{ bgcolor: "white", minHeight: "100vh", position: "relative" }}>
      {/* Back & Favorite */}
      <IconButton
  onClick={() => {
    navigate("/");      // go to root route (no college URL)
    setTimeout(() => {
      openSearch();     // open modal on root
    }, 50);
  }}
  sx={{ position: "absolute", top: 16, left: 16 }}
>
  <ArrowBackIcon />
</IconButton>
      
<IconButton
  onClick={() => setVideoOpen(true)}
  sx={{
    position: "absolute",
    top: 16,
    right: 16,
    padding: "6px",
    color: "#555",
    backgroundColor: "transparent",
    transition: "color 0.2s ease",

    "&:hover": {
      backgroundColor: "transparent",
      color: "#007bff"
    }
  }}
>
  <InfoOutlinedIcon fontSize="small" />
</IconButton>

      {/* Logo */}
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <img
          src={getUniversityLogo(college.University)}
          alt={college.University}
          style={{ height: 140, objectFit: "contain" }}
        />
      </Box>

      {/* Banner */}
      <Box
        sx={{
          bgcolor: "#1976d2",
          color: "white",
          px: 2,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
          {college.Institute_Name}
        </Typography>
        <Box textAlign="right">
          <Typography sx={{ fontWeight: "bold", fontSize: "1.2rem", lineHeight: 1.2 }}>
            {college.Institute_Code}
          </Typography>
          <Typography variant="caption">Institute Code</Typography>
        </Box>

        {college.Web_Address && (
          <Fab
            color="warning"
            size="medium"
            sx={{ position: "absolute", right: 16, bottom: -28 }}
            onClick={() => openWebsite(college.Web_Address)}
          >
            <OpenInNewIcon />
          </Fab>
        )}
      </Box>

      {/* College details */}
      <Box sx={{ px: 2, mt: 4 }}>
        {[
          ["University", college.University],
          ["Status", college.Status],
          ["Autonomy Status", college.Autonomy_Status],
          ["Gender", college.Gender || "Co-Education"],
          ["Total Fee", college.Fee || "N/A"],
          ["Total Intake", college.Total_Intake || "N/A"],
          ["Region", college.Region],
          ["District", college.District],
          ["Address", college.Address || "N/A"],
        ].map(([label, value], idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              py: 1,
              borderBottom: "1px solid #eee",
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>{label}:</Typography>
            <Typography sx={{ textAlign: "right", maxWidth: "60%" }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Add Compare button */}
      <Box sx={{ px: 2, mt: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleAddToCompare}
          fullWidth
          sx={{ borderRadius: 2 }}
        >
          Add to Compare
        </Button>
      </Box>

      {/* Courses accordion */}
      <Box sx={{ px: 2, mt: 3 }}>
        {(courses || []).map((course, index) => {
          const code = getCourseCode(course);
          return (
            <Accordion
              key={code || index}
              expanded={expandedIndex === index}
              onChange={() => setExpandedIndex(expandedIndex === index ? false : index)}
              sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: "#1976d2" }}>
                      {course.Course_Name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Intake: {course.Total_Intake || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Stack direction="row" spacing={1}>
                  {[1, 2, 3, 4].map((r) => (
                    <Chip
                      key={r}
                      label={`CAP ${r}`}
                      clickable
                      color={
                        selectedRound === r && currentChoiceCode === code
                          ? "primary"
                          : "default"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        loadCutoff(course, code, r);
                      }}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>


      {/* Cutoff Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={cutoffDrawerOpen}
        onClose={() => setCutoffDrawerOpen(false)}
        onOpen={() => {}} 
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, height: "70%" } }}
      >
        <Box sx={{ px: 2, pt: 1, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Box sx={{ width: 64, height: 6, bgcolor: "#999", borderRadius: 3 }} />
          </Box>

          {/* Drawer header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Box>
              <Typography sx={{ color: "#1976d2", fontWeight: 700, fontSize: "1.05rem" }}>
                {currentCourse?.Course_Name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Intake: {currentCourse?.Total_Intake || "N/A"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Paper sx={{ px: 1.2, py: 0.6, borderRadius: 2, bgcolor: "#f0f0f0" }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {currentChoiceCode}
                </Typography>
              </Paper>
              <Tooltip title="Copy Choice Code">
                <IconButton onClick={() => copyToClipboard(currentChoiceCode)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ mb: 1 }} />

          {/* Filters */}
          <Box sx={{ overflowX: "auto", mb: 1 }}>
            <Stack direction="row" spacing={1}>
              {ALL_FILTERS.map((f) => {
                const selected = selectedFilters.includes(f);
                return (
                  <Chip
                    key={f}
                    label={f}
                    clickable
                    variant={selected ? "filled" : "outlined"}
                    color={selected ? "primary" : "default"}
                    onClick={() => toggleFilter(f)}
                    sx={{ borderRadius: 3, px: 1.8, py: 0.8, fontWeight: 600 }}
                  />
                );
              })}
            </Stack>
          </Box>

          {/* Cutoff table */}
          {loadingCutoff ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress size={20} />
              <Typography sx={{ ml: 1 }} variant="body2">
                Loading cutoffs...
              </Typography>
            </Box>
          ) : cutoffRows.length === 0 ? (
            <Box sx={{ textAlign: "center", mt: 6, color: "text.secondary" }}>
              <Typography>No cutoff data available for CAP {selectedRound}.</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowY: "auto", flex: 1 }}>
              <Paper>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Rank</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        Percentile
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cutoffRows
                      .filter((row) => rowMatchesSelectedFilters(row))
                      .map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.Category}</TableCell>
                          <TableCell>{row.Rank}</TableCell>
                          <TableCell align="right">{row.Percentile}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <Typography variant="caption" sx={{ display: "block", textAlign: "center", py: 1 }}>
                  Cut off is based on CAP Round {selectedRound} (2025-26)
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </SwipeableDrawer>
      <Dialog
  open={videoOpen}
  onClose={() => setVideoOpen(false)}
  maxWidth="md"
  fullWidth
>
  <DialogTitle
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }}
  >
    <Typography variant="h6">
      Watch Tutorial
    </Typography>

    <IconButton onClick={() => setVideoOpen(false)}>
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  <DialogContent dividers sx={{ p: 0 }}>
    <Box
      sx={{
        position: "relative",
        width: "100%",
        paddingTop: "56.25%",
        backgroundColor: "#000"
      }}
    >
      <iframe
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0
        }}
        src="https://player.vimeo.com/video/1166015267?badge=0&autopause=0&player_id=0&app_id=58479"
        title="Tutorial Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </Box>
  </DialogContent>
</Dialog>
    </Box>
  );
};
export { getGOPENSCutoffsByCollegeId };
export default CollegeDetail;
